import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { X, ShoppingCart, Check, Minus, Star, Trash2, Plus, GitCompare, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get session ID for comparison (same as ProductDetailPage)
const getSessionId = () => {
  let sessionId = localStorage.getItem('comparison_session');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}`;
    localStorage.setItem('comparison_session', sessionId);
  }
  return sessionId;
};

function ComparePage() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const sessionId = getSessionId();

  const fetchCompareProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/compare?session_id=${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompareProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const removeFromComparison = async (productId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/compare/remove/${productId}?session_id=${sessionId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setProducts(prev => prev.filter(p => p.id !== productId));
        toast.success('Removed from comparison');
      }
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const clearComparison = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/compare/clear?session_id=${sessionId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setProducts([]);
        toast.success('Comparison cleared');
      }
    } catch (error) {
      toast.error('Failed to clear');
    }
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      discount_price: product.discount_price,
      image: product.images?.[0] || product.image
    }, 1);
    toast.success('Added to cart!');
  };

  // Get all unique specification keys
  const getSpecKeys = () => {
    const keys = new Set();
    products.forEach(p => {
      if (p.specifications) {
        Object.keys(p.specifications).forEach(key => keys.add(key));
      }
      if (p.key_features) {
        keys.add('Key Features');
      }
    });
    return Array.from(keys);
  };

  const formatSpecKey = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Compare Products</h1>
            <p className="text-gray-600">Compare up to 4 products side by side</p>
          </div>
          {products.length > 0 && (
            <button
              onClick={clearComparison}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 size={18} />
              Clear All
            </button>
          )}
        </div>

        {products.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
            <GitCompare className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No products to compare</h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Add products to compare by clicking the &quot;Compare&quot; button on product pages
            </p>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Products Header Row */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b">
                    <th className="w-48 p-4 text-left font-medium text-gray-600 bg-gray-50">
                      Product
                    </th>
                    {products.map(product => (
                      <th key={product.id} className="p-4 relative">
                        <button
                          onClick={() => removeFromComparison(product.id)}
                          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <X size={18} />
                        </button>
                        <Link to={`/product/${product.id}`} className="block">
                          <div className="w-32 h-32 mx-auto mb-3 bg-gray-50 rounded-lg overflow-hidden">
                            <img
                              src={product.images?.[0] || product.image || '/placeholder.jpg'}
                              alt={product.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1 hover:text-blue-600">
                            {product.name}
                          </h3>
                          <p className="text-xs text-gray-500">{product.brand}</p>
                        </Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Price Row */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-gray-600 bg-gray-50">Price</td>
                    {products.map(product => (
                      <td key={product.id} className="p-4 text-center">
                        <div className="space-y-1">
                          {product.discount_price ? (
                            <>
                              <p className="text-lg font-bold text-green-600">
                                ₹{product.discount_price?.toLocaleString()}
                              </p>
                              <p className="text-sm text-gray-400 line-through">
                                ₹{product.price?.toLocaleString()}
                              </p>
                              <p className="text-xs text-green-600 font-medium">
                                {Math.round((1 - product.discount_price / product.price) * 100)}% off
                              </p>
                            </>
                          ) : (
                            <p className="text-lg font-bold text-gray-900">
                              ₹{product.price?.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Rating Row */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-gray-600 bg-gray-50">Rating</td>
                    {products.map(product => (
                      <td key={product.id} className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium">{product.rating || '4.5'}</span>
                          <span className="text-gray-400 text-sm">
                            ({product.reviews_count || 0})
                          </span>
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Stock Row */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-gray-600 bg-gray-50">Availability</td>
                    {products.map(product => (
                      <td key={product.id} className="p-4 text-center">
                        {product.stock > 0 ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <Check size={16} />
                            In Stock
                          </span>
                        ) : (
                          <span className="text-red-600">Out of Stock</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Key Features */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-gray-600 bg-gray-50">Key Features</td>
                    {products.map(product => (
                      <td key={product.id} className="p-4">
                        {product.key_features?.length > 0 ? (
                          <ul className="text-sm text-gray-600 space-y-1">
                            {product.key_features.slice(0, 5).map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <Check size={14} className="text-green-500 mt-0.5 shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Specifications */}
                  {getSpecKeys().filter(k => k !== 'Key Features').map(key => (
                    <tr key={key} className="border-b">
                      <td className="p-4 font-medium text-gray-600 bg-gray-50">
                        {formatSpecKey(key)}
                      </td>
                      {products.map(product => (
                        <td key={product.id} className="p-4 text-center text-sm">
                          {product.specifications?.[key] || (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Add to Cart Row */}
                  <tr>
                    <td className="p-4 font-medium text-gray-600 bg-gray-50">Action</td>
                    {products.map(product => (
                      <td key={product.id} className="p-4 text-center">
                        <button
                          onClick={() => handleAddToCart(product)}
                          disabled={!product.stock}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          <ShoppingCart size={16} />
                          Add to Cart
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add More Products */}
        {products.length > 0 && products.length < 4 && (
          <div className="mt-6 text-center">
            <p className="text-gray-600 mb-3">
              You can add {4 - products.length} more product{4 - products.length > 1 ? 's' : ''} to compare
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              <Plus size={18} />
              Add More Products
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default ComparePage;
